import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LayoutGrid, List, Plus } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Button } from "@/components/ui/button";
import FullScreenLoader from "@/components/ui/full-screen-loader";
import { useAppData } from "@/contexts/AppDataContext";
import { useViewerReadOnly } from "@/contexts/ViewerReadOnlyContext";
import { AddHeroAsinModal } from "@/components/shared/AddHeroAsinModal";
import { DeleteHeroAsinDialog, DeleteHeroAsinTarget } from "@/components/shared/DeleteHeroAsinDialog";
import { PageTitle } from "@/components/shared/PageTitle";
import { SearchFilterBar, type FilterColumn } from "@/components/shared/SearchFilterBar";
import { LifecycleStateModal } from "@/components/shared/LifecycleStateModal";
import { ProductsCardView } from "@/components/products/ProductsCardView";
import { ProductsTableView } from "@/components/products/ProductsTableView";
import { FullCatalogTab } from "@/components/products/FullCatalogTab";
import {
  ViewMode,
  ProductsTab,
  type ProductSortField,
  VIEW_MODE_STORAGE_KEY,
  filterProducts,
  filterProductsByLifecycle,
  sortProducts,
} from "@/components/products/productsConstants";
import { LIFECYCLE_LABELS, LIFECYCLE_STATES } from "@/constants/product";
import { RoutePath } from "@/routes";
import { productApi } from "@/resources/product";
import type { ProductListItem } from "@/types";
import { handleApiError } from "@/utils";
import { getNextSort, type SortDirection } from "@/components/shared/SortableTableHead";

// Full Catalog is disabled pending a real brand-catalog-import pipeline
// (match by brand name + BSR filter). Today it just reuses
// landscape_products.brand_id, which means "captured while scraping this
// brand's landscapes," not "this brand's actual retail catalog" -- so it
// surfaces every competitor ASIN ever seen, not the brand's own products.
// Flip this back on once that pipeline exists; everything else here is
// already wired for it.
// AUT-256: when re-enabled, convert the Signal Products / Full Catalog tabs below
// (Tabs/TabsList/TabsTrigger) to links with the active tab in the URL, per the pattern
// in BrandIntelligence.tsx / ProductDetail.tsx -- they're dead code today so they were
// deliberately left as Radix tabs rather than converted along with those two screens.
const FULL_CATALOG_ENABLED = false;

/**
 * The page's only filter group, and the first this screen has ever had: `SearchFilterBar` renders
 * its `FilterDropdown` only when `filterColumns`, `activeFilters` and `onFiltersChange` are all
 * passed (`SearchFilterBar.tsx:39-45`), so the dropdown, its chips and its `Clear all` appear here
 * for the first time holding this one group.
 *
 * Multi-select, with no explicit "All": an empty selection is "All", which is what
 * `filterProductsByLifecycle` implements (`design-input/product-lifecycle-states/
 * prototype-extracted.md § The filter`). Module-level because it is derived from two constants and
 * would otherwise be a fresh array on every render.
 */
const LIFECYCLE_FILTER_COLUMNS: FilterColumn[] = [
  {
    id: "lifecycle",
    label: "Lifecycle State",
    options: LIFECYCLE_STATES.map((state) => ({ value: state, label: LIFECYCLE_LABELS[state] })),
  },
];

export default function Products() {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [asinToDelete, setAsinToDelete] = useState<DeleteHeroAsinTarget | null>(null);
  const [activeTab, setActiveTab] = useState<ProductsTab>(ProductsTab.HERO);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window === "undefined") return ViewMode.CARD;
    const saved = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    return saved === ViewMode.TABLE || saved === ViewMode.CARD ? saved : ViewMode.CARD;
  });
  const [search, setSearch] = useState("");
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({});
  const [sortField, setSortField] = useState<ProductSortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDirection>("desc");
  /**
   * The one dialog's target. Every entry point — the table row's kebab, the card's kebab and the
   * badge's `Change lifecycle state` footer on either view — arrives here through the same
   * `onSetLifecycleRequest`, so the page hosts exactly one `LifecycleStateModal`
   * (`components/product.md § The Products page — four surfaces, one write path`).
   */
  const [lifecycleTarget, setLifecycleTarget] = useState<ProductListItem | null>(null);

  const navigate = useNavigate();
  const { selectedBrandId } = useAppData();
  const readOnly = useViewerReadOnly();

  const {
    data: products = [],
    isFetching: isHeroProductListFetching,
  } = productApi.useGetHeroProductList(selectedBrandId ?? undefined);

  const { data: allProducts = [], isLoading: isAllProductsLoading } = productApi.useGetAllProductList(
    FULL_CATALOG_ENABLED && activeTab === ProductsTab.CATALOG ? (selectedBrandId ?? undefined) : undefined,
  );

  const { mutateAsync: addHeroProductsAsync } = productApi.useAddHeroProducts();

  useEffect(() => {
    window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
  }, [viewMode]);

  const isLoadingProductList =
    selectedBrandId != null && selectedBrandId > 0 && isHeroProductListFetching;

  const handleAddHeroAsins = async (asins: string[]) => {
    if (!selectedBrandId) return;
    try {
      const result = await addHeroProductsAsync({ brandId: selectedBrandId, asins });
      const { title, description } = productApi.describeAddHeroProductsResult(result);
      toast.success(title, { description });
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleSort = (field: ProductSortField) => {
    const next = getNextSort(sortField, sortDir, field);
    setSortField(next.field);
    setSortDir(next.direction);
  };

  // AUT-363: `subPath` lets a row's "View all" open the tab it counted (A/B tests, or Keywords'
  // Master List) instead of the product's default tab.
  const handleNavigate = (productId: number, subPath = "") => {
    navigate(`${RoutePath.Products}/${productId}${subPath}`);
  };

  // Search, then lifecycle, then sort. Both filters are client-side over the list already in the
  // cache -- neither adds a request, and sorting keeps its existing client-side path
  // (`intent.md § Edges considered`, *Volume*).
  const displayedProducts = useMemo(() => {
    const searched = filterProducts(products, search);
    const filtered = filterProductsByLifecycle(searched, activeFilters.lifecycle ?? []);
    return sortProducts(filtered, sortField, sortDir);
  }, [products, search, activeFilters, sortField, sortDir]);

  if (isLoadingProductList) {
    return <FullScreenLoader />;
  }

  const heroContent = (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {products.length > 0 ? (
          <>
            <SearchFilterBar
              search={search}
              onSearchChange={setSearch}
              placeholder="Search products, ASINs…"
              searchWidth="w-80"
              filterColumns={LIFECYCLE_FILTER_COLUMNS}
              activeFilters={activeFilters}
              onFiltersChange={setActiveFilters}
            />
            <div className="flex items-center gap-2">
              <ToggleGroup
                type="single"
                value={viewMode}
                onValueChange={(v) => v && setViewMode(v as ViewMode)}
                className="border border-border rounded-lg p-0.5"
              >
                <ToggleGroupItem
                  value={ViewMode.CARD}
                  aria-label="Card view"
                  className="h-8 w-8 p-0 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                >
                  <LayoutGrid className="h-4 w-4" />
                </ToggleGroupItem>
                <ToggleGroupItem
                  value={ViewMode.TABLE}
                  aria-label="Table view"
                  className="h-8 w-8 p-0 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                >
                  <List className="h-4 w-4" />
                </ToggleGroupItem>
              </ToggleGroup>
              {!readOnly && (
                <Button size="sm" onClick={() => setAddModalOpen(true)} className="gap-1.5">
                  <Plus className="h-4 w-4" />
                  Add products to Signal
                </Button>
              )}
            </div>
          </>
        ) : (
          !readOnly && (
            <Button size="sm" onClick={() => setAddModalOpen(true)} className="gap-1.5 ml-auto">
              <Plus className="h-4 w-4" />
              Add products to Signal
            </Button>
          )
        )}
      </div>

      {viewMode === ViewMode.CARD ? (
        <ProductsCardView
          products={displayedProducts}
          readOnly={readOnly}
          onNavigate={handleNavigate}
          onDeleteRequest={setAsinToDelete}
          onSetLifecycleRequest={setLifecycleTarget}
        />
      ) : (
        <ProductsTableView
          products={displayedProducts}
          readOnly={readOnly}
          sortField={sortField}
          sortDir={sortDir}
          onSort={handleSort}
          onNavigate={handleNavigate}
          onDeleteRequest={setAsinToDelete}
          onSetLifecycleRequest={setLifecycleTarget}
        />
      )}
    </div>
  );

  return (
    <>
      <div className="-m-9 p-9 bg-card space-y-6 min-h-screen">
        <div>
          <PageTitle title="Products" brandScoped />
          <p className="text-sm text-muted-foreground mt-1">
            Select a product to manage its keyword bank, landscapes, SEO insights, and A/B tests.
          </p>
        </div>

        {FULL_CATALOG_ENABLED ? (
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as ProductsTab)} className="space-y-6">
            <TabsList>
              <TabsTrigger value={ProductsTab.HERO}>Signal Products</TabsTrigger>
              <TabsTrigger value={ProductsTab.CATALOG}>Full Catalog</TabsTrigger>
            </TabsList>

            <TabsContent value={ProductsTab.HERO} className="mt-0">
              {heroContent}
            </TabsContent>

            <TabsContent value={ProductsTab.CATALOG} className="mt-0">
              <FullCatalogTab
                heroProducts={products}
                allProducts={allProducts}
                readOnly={readOnly}
                isLoading={isAllProductsLoading}
                onAddToSignal={handleAddHeroAsins}
              />
            </TabsContent>
          </Tabs>
        ) : (
          heroContent
        )}
      </div>

      {!readOnly && (
        <AddHeroAsinModal
          open={addModalOpen}
          onOpenChange={setAddModalOpen}
          onAddAsins={handleAddHeroAsins}
          title="Add products to Signal"
          description="Paste one or more ASINs below (separated by spaces, commas, or new lines)."
          secondaryAction={
            FULL_CATALOG_ENABLED && activeTab !== ProductsTab.CATALOG
              ? { label: "Browse Full Catalog", onClick: () => setActiveTab(ProductsTab.CATALOG) }
              : undefined
          }
        />
      )}

      {/*
        One dialog for the whole page, the single write path for the designation. Its target is set
        by `onSetLifecycleRequest` from whichever view is mounted, and by the badge's
        `Change lifecycle state` footer, which reaches here through that same callback (`WEB-005`,
        `WEB-006`). Brand-gated like the delete dialog beside it, and not additionally `readOnly`-
        gated: a viewer is handed no callback by either view, so nothing can set a target
        (`intent.md § FR-9`).

        Saving deliberately patches the cached row rather than invalidating
        `["hero","product","list",brandId]` -- this page binds that query's `isFetching` and returns
        a `<FullScreenLoader />` from it, so an invalidation would blank the page mid-save
        (`decisions/a-lifecycle-save-patches-the-cached-product-row.md`,
        `tech-debt.md § hero-delete-invalidation-blanks-the-products-page`).
      */}
      {selectedBrandId && (
        <LifecycleStateModal
          open={lifecycleTarget !== null}
          onOpenChange={(open) => {
            if (!open) setLifecycleTarget(null);
          }}
          product={lifecycleTarget}
          brandId={selectedBrandId}
        />
      )}

      {selectedBrandId && (
        <DeleteHeroAsinDialog
          asin={asinToDelete}
          brandId={selectedBrandId}
          onCancel={() => setAsinToDelete(null)}
          onDeleted={() => setAsinToDelete(null)}
        />
      )}
    </>
  );
}
